import { InsuranceEntryState } from 'domains/signupv3/Insurance/insuranceMachineV3';
import { SignupState } from 'domains/signupv3/Parent/signupMachineV3';
import { ScheduleState } from 'domains/signupv3/schedule/scheduleMachineV3';
import { MiniScreenerEntryState } from 'domains/signupv3/Mini-Screener/miniScreenerMachine';
import mixpanel from 'mixpanel-browser';

const SIGNUP_EVENT_SUFFIX = 'Signup v3';

const TRACKED_SIGNUP_STATES: {
  [k in SignupState | InsuranceEntryState | ScheduleState | MiniScreenerEntryState]?:
    | string
    | { name: string; props?: Record<string, unknown> };
} = {
  /** all states are in the flow order */

  // client / contact info
  enterState: `State - ${SIGNUP_EVENT_SUFFIX}`,
  enterName: `Name - ${SIGNUP_EVENT_SUFFIX}`,
  enterPhoneNumber: `Phone Number - ${SIGNUP_EVENT_SUFFIX}`,
  enterEmail: `Email - ${SIGNUP_EVENT_SUFFIX}`,
  creatingLead: `Create Lead - ${SIGNUP_EVENT_SUFFIX} - In Progress`,
  leadCreationFailed: `Create Lead - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  enterTarget: `Target - ${SIGNUP_EVENT_SUFFIX}`,

  // miniscreener
  enteringMiniScreener: `Start Mini Screener - ${SIGNUP_EVENT_SUFFIX}`,
  enterClientName: `Client Name - ${SIGNUP_EVENT_SUFFIX}`,
  enterDob: `Date of Birth - ${SIGNUP_EVENT_SUFFIX}`,
  enterRelationshipToClient: `Relationship to Client - ${SIGNUP_EVENT_SUFFIX}`,
  enterConcerns: `Communication Concerns - ${SIGNUP_EVENT_SUFFIX}`,
  enterPrimaryLanguage: `Primary Language - ${SIGNUP_EVENT_SUFFIX}`,
  enterPreviousEvaluation: `Previous Evaluation - ${SIGNUP_EVENT_SUFFIX}`,

  // insurance
  enteringInsurance: `Start Insurance - ${SIGNUP_EVENT_SUFFIX}`,
  enterUseInsurance: `Uses Insurance (yes/no) - ${SIGNUP_EVENT_SUFFIX}`,
  enterInsuranceType: `Insurance Type - ${SIGNUP_EVENT_SUFFIX}`,
  enterMedicaidProvider: `Medicaid Provider - ${SIGNUP_EVENT_SUFFIX}`,
  insuranceNotInNetwork: `Insurance Not-In-Network - ${SIGNUP_EVENT_SUFFIX}`,

  // start schedule process
  enteringSchedule: `Start Schedule - ${SIGNUP_EVENT_SUFFIX}`,
  enterAppointmentType: {
    name: `Choose Appointment Type - ${SIGNUP_EVENT_SUFFIX}`,
    props: {
      'Eligible for Evaluation': true,
    },
  },

  // consultation appointment
  redirectToConsultation: {
    name: `Redirect User to Consultation - ${SIGNUP_EVENT_SUFFIX}`,
    props: {
      'Eligible for Evaluation': false,
    },
  },
  loadingAvailableConsultDates: `Available Consultation Dates - ${SIGNUP_EVENT_SUFFIX} - Loading`,
  loadingAvailableConsultDatesFailed: `Available Consultation Dates - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  enterConsultationDate: `Pick Consultation Date - ${SIGNUP_EVENT_SUFFIX}`,
  confirmConsultationDate: `Confirm Consultation Reservation - ${SIGNUP_EVENT_SUFFIX}`,
  schedulingConsultation: `Schedule Consultation - ${SIGNUP_EVENT_SUFFIX} - In Progress`,
  schedulingConsultationFailed: `Schedule Consultation - ${SIGNUP_EVENT_SUFFIX} - Failed`,

  // evaluation appointment
  enterEvaluationDate: `Pick Evaluation Date - ${SIGNUP_EVENT_SUFFIX}`,
  loadingAvailableEvalDates: `Available Evaluation Dates - ${SIGNUP_EVENT_SUFFIX} - Loading`,
  loadingAvailableEvalDatesFailed: `Available Evaluation Dates - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  loadingEvaluationPrice: `Evaluation Price - ${SIGNUP_EVENT_SUFFIX} - Loading`,
  loadingEvaluationPriceFailed: `Evaluation Price - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  loadingStripeAccountId: `Stripe Account ID - ${SIGNUP_EVENT_SUFFIX} - Loading`,
  loadingStripeAccountIdFailed: `Stripe Account ID - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  confirmEvaluationDate: `Confirm Evaluation Reservation - ${SIGNUP_EVENT_SUFFIX}`,
  schedulingEvaluation: `Schedule Evaluation - ${SIGNUP_EVENT_SUFFIX} - In Progress`,
  schedulingEvaluationFailed: `Schedule Evaluation - ${SIGNUP_EVENT_SUFFIX} - Failed`,
  contactSupport: `Contact Support - ${SIGNUP_EVENT_SUFFIX} - Failed on Evaluation Schedule`,

  // final state
  signupComplete: `Signup End of Flow - ${SIGNUP_EVENT_SUFFIX} - Completed`, // redirecting to intake or thank you page
};

export const signupV3StateChangeListener = (
  currentState: SignupState | InsuranceEntryState | ScheduleState | MiniScreenerEntryState,
) => {
  const mappedEvent = TRACKED_SIGNUP_STATES[currentState];

  if (mappedEvent) {
    if (typeof mappedEvent === 'object') {
      mixpanel.track(mappedEvent.name, mappedEvent.props);
    } else {
      mixpanel.track(mappedEvent);
    }
  }
};
